import { Stack } from '@mui/material';
import { Genres, ProductGroups, Routes } from 'enums';
import FeatureItemAdvanced from 'features/feature-overview/feature-item-advanced';
import ProductGroup from 'features/feature-overview/product-group';
import TextGenerationProjectOverview from 'features/text-generation/text-generation-projects-overview/text-generation-project-overview';
import { useUserData } from 'hooks/use-user-data';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import useAppBarNavigationStore, { NavigationStates } from 'stores/app-bar-navigation-store';
import { makeStyles } from 'tss-react/mui';
import { isProductHidden } from 'utils/products.utils';

const HrBooster: React.FC = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { appBarNavigation, setAppBarNavigation } = useAppBarNavigationStore((state) => ({
    appBarNavigation: state.activeTab,
    setAppBarNavigation: state.setActiveTab,
  }));

  const { createdAt, products } = useUserData();

  const hrBoosterGenres = [Genres.ACTIVE_SOURCING, Genres.JOB_OFFER];

  const handleNavigation = (route: Routes, activeTab: NavigationStates = NavigationStates.TOOL) => {
    return () => {
      navigate(route);
      setAppBarNavigation(activeTab);
    };
  };

  return (
    <div className={classes.hrBooster}>
      {appBarNavigation === NavigationStates.TOOL ? (
        <Stack gap={8}>
          <ProductGroup productGroup={ProductGroups.HR_BOOSTER} productGroupName={t('hrBoosterActiveSourcing')}>
            <FeatureItemAdvanced
              productGroup={ProductGroups.HR_BOOSTER}
              featureName={t('hrBoosterAutoCampaignProduct')}
              descriptionPrimary={t('hrBoosterAutoCampaignProductDescriptionMain')}
              descriptionSecondary={t('hrBoosterAutoCampaignProductDescriptionSub')}
              features={[t('fullyAutomated'), t('aiTextGeneration'), t('matchPersonalityProfile')]}
              action={handleNavigation(Routes.RECRUITING_BOOSTER_CO_PILOT_PROJECTS, NavigationStates.PROJECTS)}
              hot={true}
            />
          </ProductGroup>
          {!isProductHidden(products, [], createdAt) ? (
            <ProductGroup productGroup={ProductGroups.HR_BOOSTER} productGroupName={t('hrBoosterJobAds')}>
              {!isProductHidden(products, [], createdAt) ? (
                <FeatureItemAdvanced
                  productGroup={ProductGroups.HR_BOOSTER}
                  featureName={`${t('jobAdsOptimization')} -\n${t('globalProfiles')}`}
                  descriptionPrimary={t('hrBoosterGlobalProfilesProductDescriptionMain')}
                  descriptionSecondary={t('hrBoosterGlobalProfilesProductDescriptionSub')}
                  features={[t('existingTargetGroups'), t('aiTextGeneration'), t('matchPersonalityProfile')]}
                  action={handleNavigation(Routes.HR_BOOSTER_GENERATE_GLOBAL_PROFILE)}
                />
              ) : null}
              {!isProductHidden(products, [], createdAt) ? (
                <FeatureItemAdvanced
                  productGroup={ProductGroups.HR_BOOSTER}
                  featureName={`${t('jobAdsOptimization')} -\n${t('tailoredProfiles')}`}
                  descriptionPrimary={t('hrBoosterTailoredProfilesProductDescriptionMain')}
                  descriptionSecondary={t('hrBoosterTailoredProfilesProductDescriptionSub')}
                  features={[t('ownTargetGroups'), t('aiTextGeneration'), t('matchPersonalityProfile')]}
                  linkedinRequired={true}
                  action={handleNavigation(Routes.HR_BOOSTER_GENERATE_TAILORED_PROFILE)}
                />
              ) : null}
            </ProductGroup>
          ) : null}
        </Stack>
      ) : appBarNavigation === NavigationStates.PROJECTS ? (
        <Stack gap={{ xs: 2, md: 1 }}>
          {hrBoosterGenres.map((genre) => (
            <TextGenerationProjectOverview key={genre} title={t(genre)} genre={genre} productGroup={ProductGroups.HR_BOOSTER} />
          ))}
        </Stack>
      ) : null}
    </div>
  );
};

const useStyles = makeStyles()((theme) => ({
  hrBooster: {
    padding: '20px 16px 16px 48px',
    [theme.breakpoints.down('lg')]: {
      padding: '20px 8px 8px 8px',
    },
  },
}));

export default HrBooster;
